/* eslint-disable @typescript-eslint/no-magic-numbers */
import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, HostBinding, inject, Input, input, numberAttribute, output, signal, type Signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { IconBigMediumWinkelLinks, IconBigMediumWinkelRechts, IconComponent } from '@big-direkt/utils/icons';
import { TranslocoDirective } from '@jsverse/transloco';
import { type PaginationState } from '../models';

@Component({
    selector: 'big-ui-pagination',
    templateUrl: './pagination.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [IconComponent, NgClass, TranslocoDirective, RouterLink],
})
export class PaginationComponent {
    public readonly iconAngleRight = IconBigMediumWinkelRechts;
    public readonly iconAngleLeft = IconBigMediumWinkelLinks;
    public readonly pageChange = output<number>();
    public readonly state: Signal<PaginationState>;
    public readonly showItemsCount = input(false);

    private readonly route = inject(ActivatedRoute);
    private readonly page = signal(1);
    private readonly size = signal(10);
    private readonly count = signal(0);

    @HostBinding('class') public classList = 'block';

    @Input({ required: true, transform: numberAttribute })
    public set currentPage(page: number) {
        this.page.set(page < 1 ? 1 : page);
    }

    @Input({ required: true, transform: numberAttribute })
    public set totalItems(count: number) {
        this.count.set(count);
    }

    @Input({ required: false, transform: numberAttribute })
    public set pageSize(size: number) {
        this.size.set(size);
    }

    public constructor() {
        this.state = computed<PaginationState>(() => {
            const pageSize = this.size();
            const totalItems = this.count();
            const totalPages = Math.ceil(totalItems / pageSize);

            let currentPage = this.page();

            if (currentPage > totalPages) {
                currentPage = totalPages;
            }

            const pages: number[] = this.createPages(currentPage, totalPages);

            return {
                url: ['/', ...this.route.snapshot.url.map(url => url.path)],
                previousPage: currentPage > 1 ? currentPage - 1 : -1,
                nextPage: currentPage < totalPages ? currentPage + 1 : -1,
                currentPage,
                pageSize,
                totalItems,
                totalPages,
                pages,
            };
        });
    }

    public handleClick(event: Event, page: number): boolean {
        event.preventDefault();

        this.pageChange.emit(page);

        return false;
    }

    // eslint-disable-next-line sonarjs/cognitive-complexity
    private createPages(currentPage: number, totalPages: number): number[] {
        const pages: number[] = [];

        let hasDotsBefore = false;
        let hasDotsAfter = false;

        for (let i = 1; i <= totalPages; i++) {
            // this is for the short version of the pager up to 4 pages and the current page
            if (i === 1 || i === totalPages || totalPages <= 4 || i === currentPage) {
                pages.push(i);
                continue;
            }

            // after current page
            if (
                i === currentPage + 1 || // add first page
                (i === currentPage + 2 && currentPage < 3) || // add second page
                (i === currentPage + 3 && currentPage < 2) // add third page
            ) {
                pages.push(i);
                continue;
            }

            if (i > currentPage && !hasDotsAfter) {
                pages.push(-1);
                hasDotsAfter = true;
                continue;
            }

            // before current page
            if (
                i === currentPage - 1 || // add first page
                (i === currentPage - 2 && currentPage + 2 > totalPages) || // add second page
                (i === currentPage - 3 && i >= totalPages - 3) // add third page
            ) {
                pages.push(i);
                continue;
            }

            if (i < currentPage && !hasDotsBefore) {
                pages.push(-1);
                hasDotsBefore = true;
            }
        }

        return pages;
    }
}
